// Copyright (c) Bentley Systems
import { Badge, Tooltip } from "@itwin/itwinui-react";
import {
  computeClientHealthStatus,
  dayFilterLabel,
  DayFilterValue,
  getQualifyingProjects,
  OnPremClientHealthStatus,
  ProjectRunStatus,
} from "./Models/on-prem-telemetry-models";

const HEALTH_STATUS_BADGE_COLOR: Record<OnPremClientHealthStatus, "positive" | "warning" | "negative" | "hollow"> = {
  Healthy: "positive",
  Degraded: "warning",
  Unhealthy: "negative",
  Unknown: "hollow",
};

const HEALTH_STATUS_DESCRIPTION: Record<OnPremClientHealthStatus, string> = {
  Healthy: "Healthy: the latest run of every project succeeded.",
  Degraded: "Degraded: the latest run of some projects failed.",
  Unhealthy: "Unhealthy: the latest run of every project failed.",
  Unknown: "Unknown: no project runs to judge by.",
};

const healthStatusTooltip = (
  projects: ProjectRunStatus[],
  status: OnPremClientHealthStatus,
  dayFilter: DayFilterValue,
): string => {
  const window = dayFilterLabel(dayFilter).toLowerCase();
  if (status === "Unknown") {
    const runs = projects.some((p) => p.isMuted) ? "non-muted project runs" : "project runs";
    return dayFilter === 0
      ? `${HEALTH_STATUS_DESCRIPTION[status]} No ${runs} reported.`
      : `${HEALTH_STATUS_DESCRIPTION[status]} No ${runs} in the ${window}.`;
  }
  const qualifying = getQualifyingProjects(projects, dayFilter);
  const failedCount = qualifying.filter((p) => !p.lastRunSuccess).length;
  return `${HEALTH_STATUS_DESCRIPTION[status]} ${failedCount} of ${qualifying.length} projects failed (${window}).`;
};

/** An on-prem client's health badge, computed from whichever project runs the caller passes in. */
const OnPremHealthStatusBadge = ({
  projects,
  dayFilter,
}: {
  projects: ProjectRunStatus[];
  dayFilter: DayFilterValue;
}) => {
  const status = computeClientHealthStatus(projects, dayFilter);
  return (
    <Tooltip content={healthStatusTooltip(projects, status, dayFilter)}>
      <Badge backgroundColor={HEALTH_STATUS_BADGE_COLOR[status]}>{status}</Badge>
    </Tooltip>
  );
};

export default OnPremHealthStatusBadge;
